'use client';

/**
 * Inicializa PostHog y captura pageviews en cada navegación del App Router.
 *
 * Next.js hace navegación client-side, así que el pageview hay que dispararlo a mano
 * en cada cambio de ruta (usePathname/useSearchParams). Corre en paralelo a GA4.
 */

import { useEffect, Suspense, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  clearAnalyticsStorage,
  COOKIE_CONSENT_EVENT,
  COOKIE_CONSENT_KEY,
  getConsentExpiryDelay,
  getCookieConsentExpiry,
  hasAnalyticsConsent,
} from '@/lib/cookie-consent';
import { disablePostHog, initPostHog, capturePageview } from '@/lib/posthog';

function PostHogPageviews({ enabled }: { enabled: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!enabled || !pathname) return;
    const qs = searchParams?.toString();
    const url = window.origin + pathname + (qs ? `?${qs}` : '');
    capturePageview(url);
  }, [enabled, pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      const consented = hasAnalyticsConsent();
      setEnabled(consented);
      if (consented) initPostHog();
      else {
        disablePostHog();
        clearAnalyticsStorage();
      }
      if (expiryTimer) clearTimeout(expiryTimer);
      const expiry = getCookieConsentExpiry();
      if (expiry) expiryTimer = setTimeout(sync, getConsentExpiryDelay(expiry));
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === COOKIE_CONSENT_KEY || event.key === null) sync();
    };
    sync();
    window.addEventListener(COOKIE_CONSENT_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      if (expiryTimer) clearTimeout(expiryTimer);
      window.removeEventListener(COOKIE_CONSENT_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return (
    <>
      {/* useSearchParams exige Suspense en el App Router. */}
      <Suspense fallback={null}>
        <PostHogPageviews enabled={enabled} />
      </Suspense>
      {children}
    </>
  );
}
